import React, { useCallback, useEffect, useState } from "react";
import { Alert, Box, Button, Chip, CircularProgress, Stack, Typography } from "@mui/material";

import { useApiClient } from "../useApiClient";
import useAuthToken from "../../functions/useAuthToken";
import type { BillingInterval, BillingStatusDto } from "../../models/License";
import type { TenantLicenseDto } from "../../models/Tenant";

type SubscriptionSummaryCardProps = {
    /** Reload whenever this becomes true (e.g. the profile dialog opening). */
    open: boolean;
    /** Navigate within the app; the caller closes the dialog first. */
    onNavigate: (path: string) => void;
};

type ChipColor = "default" | "success" | "warning" | "error" | "info";

type Summary = {
    chip: { label: string; color: ChipColor };
    detail: string;
    action: { label: string; kind: "portal" | "page"; emphasis: "primary" | "quiet"; color?: "error" | "primary" };
};

const SUBSCRIPTION_PAGE = "/admin/tenant";

function formatDate(value?: string | null) {
    if (!value) return "—";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

const describeInterval = (interval?: BillingInterval | null) =>
    interval === "month" ? "Monthly" : interval === "year" ? "Annual" : null;

/** HTTP status from an API error, if useApiClient exposes it. Adjust if your client uses another shape. */
function getStatus(err: any): number | undefined {
    return err?.response?.status ?? err?.status ?? err?.statusCode;
}

/** One status line + one action, in priority order: what needs attention first. */
function buildSummary(license: TenantLicenseDto, billing: BillingStatusDto | null): Summary {
    const isTrial = (license.tier ?? "").toUpperCase() === "TRIAL";
    const ends = formatDate(billing?.renewsOrEndsOnUtc ?? license.effectiveToUtc);

    if (billing?.paymentIssue) {
        return {
            chip: { label: "Payment failed", color: "error" },
            detail: "Your last payment didn't go through. Update your card to keep access.",
            action: { label: "Update card", kind: "portal", emphasis: "primary", color: "error" },
        };
    }
    if (license.isExpired) {
        return {
            chip: { label: "Expired", color: "error" },
            detail: `Ended ${formatDate(license.effectiveToUtc)}. You can view your data but not add or edit.`,
            action: { label: isTrial ? "Choose a plan" : "Renew", kind: "page", emphasis: "primary" },
        };
    }
    if (billing?.cancelAtPeriodEnd) {
        return {
            chip: { label: "Cancelled", color: "warning" },
            detail: `Full access until ${ends}, then the subscription ends.`,
            action: { label: "Resume subscription", kind: "page", emphasis: "primary" },
        };
    }
    if (isTrial) {
        const days = license.daysRemaining;
        return {
            chip: { label: days > 0 ? `${days} day${days === 1 ? "" : "s"} left` : "Ends today", color: days <= 3 ? "warning" : "info" },
            detail: days > 0 ? `Ends in ${days} day${days === 1 ? "" : "s"} (${formatDate(license.effectiveToUtc)}).` : `Ends ${formatDate(license.effectiveToUtc)}.`,
            action: { label: "Choose a plan", kind: "page", emphasis: "primary" },
        };
    }
    if (billing?.hasSubscription) {
        return {
            chip: { label: "Active", color: "success" },
            detail: `Renews automatically on ${ends}.`,
            action: { label: "Manage subscription", kind: "page", emphasis: "quiet" },
        };
    }
    // Manually granted licence (not billed through Stripe).
    return {
        chip: { label: license.isExpiringSoon ? "Expiring soon" : "Active", color: license.isExpiringSoon ? "warning" : "success" },
        detail: `Valid until ${formatDate(license.effectiveToUtc)}.`,
        action: { label: license.isExpiringSoon ? "Renew" : "Manage subscription", kind: "page", emphasis: license.isExpiringSoon ? "primary" : "quiet" },
    };
}

/**
 * Key subscription facts for the signed-in user's OWN tenant, loaded from the same license-holder
 * endpoints as the License Management page (not from user.tenantHealth).
 *
 * Renders nothing for users who aren't a tenant license holder (the API answers 401/403).
 * Shows a short error if the user is a license holder but their tenant or licence can't be found.
 */
const SubscriptionSummaryCard: React.FC<SubscriptionSummaryCardProps> = ({ open, onNavigate }) => {
    const { get, postJson } = useApiClient();
    const getAccessToken = useAuthToken();

    const [state, setState] = useState<"loading" | "hidden" | "error" | "ready">("loading");
    const [license, setLicense] = useState<TenantLicenseDto | null>(null);
    const [billing, setBilling] = useState<BillingStatusDto | null>(null);
    const [busy, setBusy] = useState(false);
    const [actionError, setActionError] = useState<string | null>(null);

    const load = useCallback(async () => {
        setState("loading");
        setActionError(null);
        setLicense(null);
        setBilling(null);

        try {
            const accessToken = await getAccessToken();
            if (!accessToken) { setState("hidden"); return; }

            // billing/status only answers for the tenant's license holder: use it to decide whether to show anything.
            let billingRes: BillingStatusDto | null = null;
            try {
                billingRes = await get<BillingStatusDto>("billing/status", accessToken);
            } catch (err: any) {
                const status = getStatus(err);
                if (status === 401 || status === 403) { setState("hidden"); return; }
                // Any other failure: billing details are optional, carry on with the licence.
            }

            let licenseRes: TenantLicenseDto | null = null;
            try {
                licenseRes = await get<TenantLicenseDto>("tenant/license", accessToken);
            } catch (err: any) {
                const status = getStatus(err);
                if (status === 401 || status === 403) { setState("hidden"); return; }
                setState("error"); // license holder, but tenant / licence missing: shouldn't happen, so say so
                return;
            }

            if (!licenseRes || !licenseRes.tier) { setState("error"); return; }

            setBilling(billingRes);
            setLicense(licenseRes);
            setState("ready");
        } catch {
            setState("error");
        }
    }, []);

    useEffect(() => {
        if (open) load();
    }, [open, load]);

    const openBillingPortal = async () => {
        setBusy(true);
        setActionError(null);
        try {
            const accessToken = await getAccessToken();
            if (!accessToken) throw new Error("Invalid access token");
            const result = await postJson<{ url: string }>("billing/portal", accessToken, {});
            window.location.assign(result.url);
        } catch {
            setActionError("Couldn't open billing. Please try again from Manage subscription.");
            setBusy(false);
        }
    };

    if (state === "hidden") return null;

    const heading = (action?: React.ReactNode) => (
        <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} mb={1}>
            <Typography variant="subtitle2">Subscription</Typography>
            {action}
        </Box>
    );

    if (state === "loading") {
        return (
            <Box>
                {heading()}
                <Box display="flex" alignItems="center" gap={1.5} py={1}>
                    <CircularProgress size={16} />
                    <Typography variant="body2" color="text.secondary">Loading subscription…</Typography>
                </Box>
            </Box>
        );
    }

    if (state === "error" || !license) {
        return (
            <Box>
                {heading()}
                <Alert
                    severity="warning"
                    action={<Button color="inherit" size="small" onClick={load}>Retry</Button>}
                >
                    We couldn't load your subscription details. If this keeps happening, please contact support.
                </Alert>
            </Box>
        );
    }

    const summary = buildSummary(license, billing);
    const interval = billing?.hasSubscription ? describeInterval(billing.billingInterval) : null;

    const actionButton = (
        <Button
            size="small"
            variant={summary.action.emphasis === "primary" ? "contained" : "outlined"}
            color={summary.action.color ?? "primary"}
            disabled={busy}
            onClick={() => (summary.action.kind === "portal" ? openBillingPortal() : onNavigate(SUBSCRIPTION_PAGE))}
            sx={{ whiteSpace: "nowrap" }}
        >
            {busy ? "Opening…" : summary.action.label}
        </Button>
    );

    return (
        <Box>
            {heading(actionButton)}

            <Stack spacing={0.75}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Typography variant="body1" fontWeight={600}>
                        {license.tier}
                    </Typography>
                    {license.planName && license.planName !== license.tier && (
                        <Typography variant="body2" color="text.secondary">
                            {license.planName}
                        </Typography>
                    )}
                    {interval && <Chip size="small" label={interval} variant="outlined" />}
                    <Chip size="small" label={summary.chip.label} color={summary.chip.color} />
                </Stack>

                <Typography variant="body2" color="text.secondary">
                    {summary.detail}
                </Typography>

                {billing?.pendingChange && (
                    <Typography variant="body2" color="text.secondary">
                        Changes to <strong>{billing.pendingChange.planName}</strong>
                        {describeInterval(billing.pendingChange.billingInterval) ? ` (${describeInterval(billing.pendingChange.billingInterval)!.toLowerCase()})` : ""}
                        {" "}on {formatDate(billing.pendingChange.effectiveAtUtc)}.
                    </Typography>
                )}

                {actionError && <Alert severity="error" sx={{ mt: 1 }}>{actionError}</Alert>}
            </Stack>
        </Box>
    );
};

export default SubscriptionSummaryCard;
